import {
  CenteredContainer,
  Column,
  Container,
  ElasticContentContainer,
  EmptyScreen,
  Row
} from '@cdab/scania/qpr/components'
import { getClient } from '@cdab/scania/qpr/contexts/backend-provider'
import { useTitle } from '@cdab/scania/qpr/contexts/title'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import { Roles, type User } from '@cdab/scania/qpr/schema'
import {
  filterTableData,
  sortTableData,
  type SortConfig
} from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter } from '@cdab/utils'
import {
  TdsBodyCell,
  TdsHeaderCell,
  TdsIcon,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
  TdsTableToolbar
} from '@scania/tegel-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  isRouteErrorResponse,
  json,
  useLoaderData,
  useNavigate,
  useRouteError
} from 'react-router'
import { StyledTdsButton } from './users.styles'
import {
  prepareTableData,
  type PreparedUserData
} from './users-table-data'

export type AdminUsersLoaderData = {
  users: User[]
}

export async function loader(): Promise<AdminUsersLoaderData> {
  const client = getClient()

  const userInfo = await client.GetuserInfo()
  if (!userInfo) {
    throw json({
      status: 401
    })
  }
  const user = await client.UserService.GetUser(userInfo.userId)
  if (user.role < Roles.FactoryEngineAdmin) {
    throw json({
      status: 403
    })
  }

  try {
    const [users] = await Promise.all([client.UserService.GetUsers()])
    return {
      users
    }
  } catch (error) {
    if (error instanceof Response) {
      throw new Response(null, {
        status: error.status
      })
    } else {
      throw new Response(null, {
        status: 404
      })
    }
  }
}

export function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'user'])
  const { updateTitles } = useTitle()
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const { users } = useLoaderData() as AdminUsersLoaderData

  const [sortConfig, setSortConfig] = useState<SortConfig>(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [processedData, setProcessedData] = useState<PreparedUserData[]>([])

  const handleTableSort = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        columnKey: string | undefined
        sortingDirection: 'asc' | 'desc'
      }>
    ) => {
      if (!event.detail.columnKey) {
        setSortConfig(null)
        return
      }
      const { columnKey, sortingDirection } = event.detail
      setSortConfig({ columnKey, sortingDirection })
    },
    []
  )

  const handleTableFilter = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        query: string | undefined
      }>
    ) => {
      if (!event.detail.query) {
        setFilterQuery('')
        return
      }
      const { query } = event.detail
      setFilterQuery(query)
    },
    []
  )

  const handleRowClick = (userId: number) => {
    navigate(`/admin/users/${userId}`)
  }

  useEffect(() => {
    let data = prepareTableData(users, t)

    if (filterQuery) {
      data = filterTableData(data, filterQuery)
    }

    if (sortConfig) {
      data = sortTableData(data, sortConfig)
    }

    setProcessedData(data)
  }, [users, sortConfig, filterQuery, t])

  useEffect(() => {
    updateTitles({
      contentHeader: {
        subtitle: t('users'),
        title: t('administration')
      },
      mobile: {
        title: `${t('users')}`,
        description: null
      }
    })
  }, [t, updateTitles])

  return (
    <ElasticContentContainer scrollY overflowHidden>
      <Container fluid='normal'>
        <Row>
          <Column width={12} padding={isLg}>
            <StyledTdsButton
              disabled={false}
              onClick={() => navigate('/admin/users/new')}
              text={t('add-new')}
              size='md'
            >
              <TdsIcon slot='icon' name='profile' />
            </StyledTdsButton>
          </Column>
          <Column width={12} padding={isLg} scrollX>
            <TdsTable responsive={true} modeVariant='secondary'>
              <TdsTableToolbar
                filter
                tableTitle={t('users')}
                onTdsFilter={handleTableFilter}
              />
              <TdsTableHeader>
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='name'
                  cellValue={t('name')}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='userName'
                  cellValue={capitalizeFirstLetter(
                    t('user-name', { ns: 'user' })
                  )}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='email'
                  cellValue={t('email', { ns: 'user' })}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='market'
                  cellValue={t('market')}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='role'
                  cellValue={t('role', { ns: 'user' })}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='editRights'
                  cellValue={t('edit-rights', { ns: 'user' })}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='status'
                  cellValue={capitalizeFirstLetter(t('status'))}
                  sortable
                />
                <TdsHeaderCell
                  onTdsSort={handleTableSort}
                  cellKey='userId'
                  cellValue='ID'
                  sortable
                />
              </TdsTableHeader>
              <TdsTableBody>
                {processedData.map((user, index) => (
                  <TdsTableBodyRow
                    clickable
                    key={user.userId || index}
                    onTdsClick={() => handleRowClick(user.userId)}
                  >
                    <TdsBodyCell cellKey='name' cellValue={user.name} />
                    <TdsBodyCell cellKey='userName' cellValue={user.userName} />
                    <TdsBodyCell cellKey='email' cellValue={user.email} />
                    <TdsBodyCell cellKey='market' cellValue={user.market} />
                    <TdsBodyCell cellKey='role' cellValue={user.role} />
                    <TdsBodyCell
                      cellKey='editRights'
                      cellValue={user.editRights}
                    />
                    <TdsBodyCell cellKey='status' cellValue={user.status} />
                    <TdsBodyCell cellKey='userId' cellValue={user.userId} />
                  </TdsTableBodyRow>
                ))}
              </TdsTableBody>
            </TdsTable>
          </Column>
        </Row>
      </Container>
    </ElasticContentContainer>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    let message = t('talk-to-admin')

    switch (error.data.status) {
      case 401:
        message = t('not-logged-in')
        break
      case 403:
        message = t('access-denied')
        break
    }

    return (
      <CenteredContainer
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column fullHeight width={12} padding={false}>
            <EmptyScreen
              title={t('could-not-load-page')}
              description={message}
              icon={<TdsIcon name='warning' size='lg' />}
            />
          </Column>
        </Row>
      </CenteredContainer>
    )
  }

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  throw error
}
